import { log, warn } from '@execonline-inc/logging';
import { when } from '@execonline-inc/maybe-adapter';
import { assertNever, identity } from '@kofno/piper';
import { Task } from 'taskarian';
import VisualizationEmbedStore from '.';
import { AppyError, callApi } from '../Appy';
import { readVarM } from '../Assertions/environments';
import { currentUserStore } from '../CurrentUserStore';
import ErrorActionableReaction, { EAProps, handleError } from '../ErrorActionableReaction';
import { warnAndNotify } from '../Honeybadger';
import { MissingLinkError, findLinkT } from '../LinkyLinky';
import { Link } from '../Resource/Types';
import {
  QuickSightEmbedAPILoadError,
  loadQuickSightEmbedAPI,
  quickSightVizFn,
} from '../components/Visualization/quickSightEmbedAPILoader';
import { DashboardType } from '../ReportStore/Types';
import { visualizationTokenResourceDecoder } from './Decoders';
import { State, VisualizationTokenResource } from './Types';

interface Props extends EAProps<VisualizationEmbedStore> {
  store: VisualizationEmbedStore;
  dashboardType: DashboardType;
  visualizationPath: string;
}
type VisualizationEmbedError = AppyError | MissingLinkError;
type VisualizationEmbedAPILoadError = QuickSightEmbedAPILoadError;

const isProduction = (candidate: string): boolean => candidate === 'production';

const warnAndMaybeNotify = readVarM('VITE_APP_ENV')
  .andThen(when(isProduction))
  .map(() => warnAndNotify)
  .getOrElseValue(warn);

const embedError =
  (store: VisualizationEmbedStore) =>
  (err: VisualizationEmbedError | VisualizationEmbedAPILoadError): void => {
    switch (err.kind) {
      case 'quicksight-embed-api-load-error':
        store.error('Unable to load report');
        warnAndMaybeNotify('QuickSightEmbedError', ` ${err.kind}`, err);
        break;
      case 'missing-link-error':
        store.error('Visualization token link is unavailable');
        break;
      default:
        handleError(store, err);
        break;
    }
  };

const loadQuickSightEmbedUrl = (
  visualizationFn: quickSightVizFn,
  dashboardId: string | undefined,
) =>
  Task.succeed<VisualizationEmbedError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('quicksight-visualizations'))
    .map((link) => ({ ...link, href: link.href.replace(`{dashboard_id}`, `${dashboardId}`) }))
    .andThen(callApi(visualizationTokenResourceDecoder, {}))
    .map((resource: VisualizationTokenResource) => ({ visualizationFn, resource }));

class VisualizationEmbedReactions extends ErrorActionableReaction<
  VisualizationEmbedStore,
  State,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    log('VisualizationEmbedState', state.kind);
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        loadQuickSightEmbedAPI()
          .mapError<VisualizationEmbedError | VisualizationEmbedAPILoadError>(identity)
          .andThen((vizFn) => {
            return loadQuickSightEmbedUrl(vizFn, this.props.visualizationPath);
          })
          .fork(embedError(store), ({ visualizationFn, resource }) =>
            store.ready(resource, visualizationFn),
          );
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default VisualizationEmbedReactions;
